import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import AdvancedSearchForm from '../../Components/GlobalSearch/AdvancedSearch/AdvanceSearchForm'
import AdvancedSearchList from '../../Components/GlobalSearch/AdvancedSearch/AdvancedSearchList'
import styles from '../../Components/GlobalSearch/GlobalSearch.module.scss'
import { BackButton, Breadcrumbs, MUIAccordion } from '../../Components/Shared'
import CONSTANTS, { BREADCRUMS } from '../../Constants'
import { API_CONFIG, API_LIST } from '../../Constants/ApiDefinations'
import { LIVE_ASSET_GROUP_ATTRIBUTES } from '../../Constants/Constant/Configs'
import { BLUK_PAGE_ROUTES } from '../../Constants/ManagePolicy'
import { getSavedFilter } from '../../Helpers/CommonListingHelper'
import { filterAdvanceSearchFields } from '../../Helpers/ManageContent'
import { deepClone, removeDuplicates } from '../../Helpers/Util'
import { BreadcrumsModel, TypeObject } from '../../Models'
import mainApiService from '../../Services'
import { useAppDispatch } from '../../Store/Hooks'
import { showAlert } from '../../Store/Slice/alert.slice'
import { setLoader } from '../../Store/Slice/loader.slice'
/**
 * Advanced Search Page Component
 *
 * @returns {React.JSX.Element} React JSX element representing the Advanced Search Page Ccomponent.
 * */

const initalFormData = {
  title: null,
  blitzId: '',
  contentCategory: [],
  assetCategory: [],
  assetType: [],
  state: [],
  countries: [],
  activePlatform: [],
  availabilityStartTime: { filter: 'equals', value: '' },
  availabilityEndTime: { filter: 'equals', value: '' },
  startsIn: { filter: 'nextDay', value: '' },
  filterIn: { filter: true, value: '' },
  expiresIn: { filter: 'nextDay', value: '' },
  firstCreatedOn: { filter: 'equals', value: '' },
  attributeFilters: [],
  assetGroupFilters: [],
  mediaInfoFilters: [],
  contentId: '',
  avsInactiveMigrated: null,
}

interface AdvancedSearchProps {
  bulkUpdatePath?: string
  targetedState?: string
  onCheckItems?: (data: any) => void
  checkBoxKey?: string
  searchReset?: any
}
const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
  bulkUpdatePath = '',
  targetedState = '',
  onCheckItems,
  checkBoxKey,
  searchReset,
}: AdvancedSearchProps) => {
  const [contentCategories, setContentCategories] = useState<TypeObject[]>([])
  const [assetCategory, setAssetCategory] = useState<TypeObject[]>([])
  const [platforms, setPlatforms] = useState<TypeObject[]>([])
  const [countries, setCountries] = useState<TypeObject[]>([])
  const [attributes, setAttributes] = useState<TypeObject[]>([])
  const [assetTypes, setAssetTypes] = useState<TypeObject[]>([])
  const [searchPayload, setSearchPayload] = useState<any>(null)
  const [searchData, setSearchData] = useState<any>([])
  const location = useLocation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pinId = location?.state?.id || searchParams.get(',id')
  const searchKey = searchParams.get('search')
  const advanceSearch = searchParams.get('advanceSearch')
  const [mounted, setMounted] = useState(false)
  const [savedFiltersData, setSavedFilterData] = useState<any>({})

  const [formData, setFormData] = useState<TypeObject>(JSON.parse(JSON.stringify(initalFormData)))

  useEffect(() => {
    if (advanceSearch || bulkUpdatePath?.length) {
      getDataSystemConfig()
      getDataMasters()
      getAssetTypeDefinition()
    }
    getAttributesList()
  }, [advanceSearch, bulkUpdatePath])

  const getPinnedSearchData = async () => {
    const requestedData = API_CONFIG(
      API_LIST.PINNED_SEARCH.LIST,
      null,
      {},
      `id=${pinId?.toString()}`,
    )
    const result = await mainApiService(requestedData as any)
    if (result?.data) {
      onSubmit({ ...result.data, page: 1, limit: 10 })
    }
  }
  useEffect(() => {
    if (BLUK_PAGE_ROUTES.includes(bulkUpdatePath)) resetSearch()
  }, [searchReset])

  const resetSearch = () => {
    setSearchPayload(null)
    setFormData(JSON.parse(JSON.stringify({ ...initalFormData, title: '', serviceAlias: '' })))
  }
  useEffect(() => {
    const handleSearch = () => {
      if (searchKey && !searchParams.get('filter')) {
        handleBasicSearch()
        return
      }

      handleAdvancedSearch()
    }

    // Helper functions
    const handleBasicSearch = () => {
      onSubmit({
        titleOrId: searchKey,
        contentFilters: [],
        attributeFilters: [],
        sort: [],
        page: 1,
        limit: 10,
      })
    }

    const handleAdvancedSearch = () => {
      const savedFilters = getSavedFilter('/advanced-search')
      if (!savedFilters) return

      processSavedFilters(savedFilters)
    }

    const processSavedFilters = (savedFilters: any) => {
      setSavedFilterData(deepClone({ ...savedFilters }))

      const hasFilterParam = searchParams.get('filter')
      if (!hasFilterParam) return

      if (savedFilters?.filterData) {
        onSubmit(savedFilters.filterData, true)
      }

      if (savedFilters?.formData && Object.keys(savedFilters.formData).length) {
        setFormData(deepClone({ ...savedFilters.formData }))
      }
    }

    if (pinId) {
      getPinnedSearchData()
    } else {
      handleSearch()
    }
  }, [pinId, searchKey])

  /**
   * To get the data of system configuration asset category and content category
   * @function getDataSystemConfig
   * @returns {void}
   */
  const getDataSystemConfig = async () => {
    const reqData = API_CONFIG(
      API_LIST.SYSTEM_CONFIGURATION.MASTER_LIST,
      {},
      null,
      'keyName=CONTENT_CATEGORY,ASSET_CATEGORY',
    )
    const result = await mainApiService(reqData)
    if (result?.data?.length) {
      const { data = [] } = result
      setContentCategories(
        data?.find((config: TypeObject) => config?.configKey === 'CONTENT_CATEGORY')?.configValue ??
          [],
      )
      setAssetCategory(
        data?.find((config: TypeObject) => config?.configKey === 'ASSET_CATEGORY')?.configValue ??
          [],
      )
    }
  }

  /**
   * To get the data of masters downstreamPlatforms and countries
   * @function getDataMasters
   * @returns {void}
   */
  const getDataMasters = async () => {
    const reqData = {
      masters: ['DOWNSTREAMPLATFORMS', 'COUNTRIES'],
      userDetails: { isFrontendReq: true },
    }

    const result = await mainApiService(
      API_CONFIG(API_LIST.MASTER_APIS.LIST_MASTERS_FETCH_ALL, reqData),
    )

    if (result?.data) {
      if (result?.data['DOWNSTREAMPLATFORMS']) {
        setPlatforms(result?.data['DOWNSTREAMPLATFORMS'])
      }
      if (result?.data['COUNTRIES']) {
        setCountries(result?.data['COUNTRIES'])
      }
    }
  }

  /**
   * To get the attribute list
   * @function getAttributesList
   * @returns {void}
   */
  const getAttributesList = async () => {
    const reqData = API_CONFIG(API_LIST.GLOBAL_SEARCH.ATTRIBUTE)
    const result: any = await mainApiService(reqData)
    if (result?.data?.length) {
      const attrData = result.data.filter((item: any) => !item?.isEMA)
      const attributeWithoutLive = attrData.filter(
        (item: any) => !LIVE_ASSET_GROUP_ATTRIBUTES.includes(item?.fieldName),
      )
      const attrList = filterAdvanceSearchFields(attributeWithoutLive)
      const attrListCopy = attrList.concat(CONSTANTS.STATIC_FIELDS_ADVANCED_FORM)
      attrListCopy.sort((a: any, b: any) => a.label.localeCompare(b.label))
      setAttributes(attrListCopy)
    }
  }

  /**
   * To get the asset type definition list
   * @function getAssetTypeDefinition
   * @returns {void}
   */
  const getAssetTypeDefinition = async () => {
    const reqData = API_CONFIG(API_LIST.GLOBAL_SEARCH.ASSET_DEFINITION)
    const result = await mainApiService(reqData)
    if (result?.data?.length) {
      const parsedData = result.data.map((item: { value: string; assetType: string }) => ({
        ...item,
        value: item.assetType,
      }))
      const uniqueAssetType = removeDuplicates(parsedData, 'assetType')
      setAssetTypes(uniqueAssetType)
    }
  }

  const setSearchParamData = () => {
    const searchParamsData: any = searchParams.toString().split(',')
    const updatedSearchParams = new URLSearchParams(searchParamsData[0])

    Object.entries({ filter: '1' }).forEach(([key, value]) => {
      if (value) {
        updatedSearchParams.set(key, value)
      } else {
        updatedSearchParams.delete(key)
      }
    })
    navigate(`?${updatedSearchParams.toString()}`)
  }

  /**
   * To submit the form on trigger could be form input or table filter change method
   * @function onSubmit
   * @param {object} data - payload data to be send in API coming from form and list.
   * @returns {void}
   */
  const onSubmit = async (data: TypeObject, from?: any, isFormFilter = false) => {
    const finalData = {
      ...data,
      sort: isFormFilter ? searchPayload?.sort || [] : data?.sort,
      limit: isFormFilter ? searchPayload?.limit || data?.limit : data?.limit,
    }
    setMounted(from)
    dispatch(setLoader(true))
    setSearchPayload(finalData)

    const reqPayload = API_CONFIG(API_LIST.GLOBAL_SEARCH.SEARCH, finalData)
    const result: any = await mainApiService(reqPayload)

    if (result?.error) {
      dispatch(showAlert([true, result?.error?.message, 'error']))
      dispatch(setLoader(false))
      return
    }

    setSearchData(result?.data)
    setSearchParamData()

    const id = document.getElementById('advanced_search_list')
    if (id?.scrollIntoView) {
      id.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
    dispatch(setLoader(false))
  }

  return (
    <>
      <div className={` ${bulkUpdatePath ? 'pt-05' : 'pl-2 pr-2 pt-1'}  advancedSearchWrapper`}>
        <div className={`${bulkUpdatePath ? 'd-none' : ''}`}>
          <Breadcrumbs
            breadcrums={
              BREADCRUMS.ADVANCED_SEARCH_BREADCRUMS(
                searchParams.get(',name') as any,
                searchParams.get('name') as any,
              ) as BreadcrumsModel[]
            }
          />
        </div>
        <div>
          <div
            className={`mt-1 d-flex justify-content-between align-items-center ${
              bulkUpdatePath ? 'd-none' : ''
            }`}
          >
            <BackButton
              label='Advanced Search'
              className='col73'
              handleOnClick={() => {
                navigate('/home')
              }}
            />
            <div
              data-testId='saved-search-navigation'
              onClick={() => {
                navigate('/home/asset-state-details/save-search', {
                  state: {
                    pathName: window.location.href?.split(window.location.host)[1],
                    state: location,
                  },
                })
              }}
              className={styles.save_search_btn}
            >
              Saved Searches
            </div>
          </div>
        </div>
        {(advanceSearch || bulkUpdatePath) && attributes?.length ? (
          <div className='pb-2 pt-1'>
            {bulkUpdatePath ? (
              <MUIAccordion
                open={true}
                title={'Search by Attribute'}
                tolltipTitle={{ collapse: 'Collapse', expand: 'Expand' }}
              >
                <AdvancedSearchForm
                  contentCategories={contentCategories}
                  attributes={attributes}
                  assetCategory={assetCategory}
                  platforms={platforms}
                  countries={countries}
                  assetTypes={assetTypes}
                  submitForm={onSubmit}
                  formData={formData}
                  setFormData={setFormData}
                  bulkUpdatePath={bulkUpdatePath}
                />
              </MUIAccordion>
            ) : (
              <AdvancedSearchForm
                contentCategories={contentCategories}
                attributes={attributes}
                assetCategory={assetCategory}
                platforms={platforms}
                countries={countries}
                assetTypes={assetTypes}
                submitForm={onSubmit}
                formData={formData}
                setFormData={setFormData}
              />
            )}
          </div>
        ) : (
          <></>
        )}
      </div>

      {searchPayload && attributes?.length ? (
        <AdvancedSearchList
          attributes={attributes}
          formData={formData}
          searchPayload={searchPayload as TypeObject[]}
          searchData={searchData}
          submitForm={onSubmit}
          mounted={mounted}
          savedFilters={savedFiltersData}
          bulkUpdatePath={bulkUpdatePath}
          targetedState={targetedState}
          onCheckItems={onCheckItems}
          checkBoxKey={checkBoxKey}
        />
      ) : (
        <></>
      )}
    </>
  )
}

export default AdvancedSearch
